// src/plugins/offline-handler.js

/**
 * Plugin for handling offline functionality in the CouponLa app.
 * This provides utilities for managing fallback images, caching, and offline notifications.
 */

import { Notify } from 'quasar'

// Placeholder image paths
const OFFLINE_IMAGE_PLACEHOLDER = '/images/offline-image-placeholder.svg'
const OFFLINE_IMAGE_PLACEHOLDER_SMALL = '/images/offline-image-placeholder.png'

export default {
  install: (app) => {
    // Add global properties accessible in all components
    app.config.globalProperties.$offlineHandler = {
      /**
       * Get appropriate image source based on connection and caching status
       * @param {string} src - Original image source
       * @param {string} size - Size category (small, medium, large)
       * @returns {string} - Appropriate image source or fallback
       */
      getImageSrc: (src, size = 'medium') => {
        if (!navigator.onLine) {
          // Return appropriate placeholder based on size
          return size === 'small' ? OFFLINE_IMAGE_PLACEHOLDER_SMALL : OFFLINE_IMAGE_PLACEHOLDER
        }
        return src
      },
      
      /**
       * Handle image loading errors and replace with offline placeholder
       * @param {Event} event - Error event from img element
       */
      handleImageError: (event) => {
        event.target.src = OFFLINE_IMAGE_PLACEHOLDER
      },
      
      /**
       * Show offline notification
       */
      notifyOffline: () => {
        Notify.create({
          message: 'You are currently offline',
          caption: 'Some features may be limited',
          icon: 'cloud_off',
          color: 'warning',
          position: 'top',
          timeout: 3000
        })
      },
      
      /**
       * Show online notification
       */
      notifyOnline: () => {
        Notify.create({
          message: 'Back online!',
          caption: 'All features are now available',
          icon: 'cloud_done',
          color: 'positive',
          position: 'top',
          timeout: 3000
        })
      },
      
      /**
       * Get fallback content for offline mode
       * @param {string} type - The type of content (coupon, store, category)
       * @returns {Object} Fallback content object
       */
      getFallbackContent: (type) => {
        const fallbacks = {
          coupon: {
            title: 'Coupon information not available offline',
            description: 'Please connect to the internet to view this coupon.',
            image: OFFLINE_IMAGE_PLACEHOLDER
          },
          store: {
            name: 'Store information not available offline',
            description: 'Connect to the internet to view store details.',
            logo: OFFLINE_IMAGE_PLACEHOLDER_SMALL
          },
          category: {
            name: 'Category not available offline',
            description: 'Connect to the internet to view this category.',
            image: OFFLINE_IMAGE_PLACEHOLDER
          }
        }
        
        return fallbacks[type] || { message: 'Content not available offline' }
      }
    }
    
    // Create a composable for using in script setup
    app.provide('offlineHandler', app.config.globalProperties.$offlineHandler)
  }
}
